import { ApiError, request } from './client';
import type { ConsoleReportData, ConsoleReportId, ConsoleReportResponse } from './types';

// Every agent key may read these; middleware decides per report. `data` is null until
// bigquery-service has copied the analysts' first nightly build.
export async function fetchConsoleReport(
  key: string,
  report: ConsoleReportId,
  signal?: AbortSignal,
): Promise<ConsoleReportData | null> {
  try {
    const response = await request<ConsoleReportResponse>(`/api/console-reports/${report}`, { key, signal });
    return response.data;
  } catch (error) {
    if (import.meta.env.DEV && error instanceof ApiError && error.status === 404)
      return fetchLocalSnapshot(report, signal);
    throw error;
  }
}

// Local development only, while the middleware it talks to does not have the route yet (404): the
// report's data saved as public/local/<report>.json, kept out of git by .gitignore. When it is
// missing the dev server answers with index.html, which is not JSON, so this resolves to null and
// the page says there is no data. A deployed build never reads it.
export const localSnapshotUrl = (report: ConsoleReportId) => `/local/${report}.json`;

async function fetchLocalSnapshot(report: ConsoleReportId, signal?: AbortSignal): Promise<ConsoleReportData | null> {
  const response = await fetch(localSnapshotUrl(report), { signal, cache: 'no-store' });
  if (!response.ok || !response.headers.get('content-type')?.includes('json')) return null;
  return (await response.json()) as ConsoleReportData;
}
